import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import MainLayout from "@/components/layout/MainLayout";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { SopMarkdown } from "@/components/manual/SopMarkdown";
import { DocBodyBoundary } from "@/components/manual/DocBodyBoundary";
import { DocFeedbackWidget } from "@/components/manual/DocFeedbackWidget";
import { ReviewerEditView, type ReviewerEditHandle } from "@/components/review/ReviewerEditView";
import { ReviewerWelcome } from "@/components/review/ReviewerWelcome";
import { SelectionFlagger } from "@/components/review/SelectionFlagger";
import { Switch } from "@/components/ui/switch";

import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  HelpCircle,
  Loader2,
  AlertCircle,
  Lightbulb,
} from "lucide-react";

type Doc = {
  id: string;
  doc_id: string;
  collection: string | null;
  domain: string;
  title: string;
  version: string;
  body_markdown: string;
  source_refs?: any;
  sort_order: number | null;
  status: string;
  canon_ready?: boolean;
  updated_at?: string;
};

type ReviewerStatus = "understood" | "confusing" | "suggested" | "edited" | "redo";

const QUEUE_KEY = "reviewer_queue_pos_v1";

// Statuses that count as "finished" for progress and auto-advance skipping.
// 'redo' is intentionally excluded: an admin sent it back, so it counts as
// unfinished and re-enters the rotation.
const FINISHED: ReviewerStatus[] = ["understood", "edited", "confusing", "suggested"];
const isFinished = (s: ReviewerStatus | undefined): boolean =>
  !!s && (FINISHED as string[]).includes(s);

function orderDocs(docs: Doc[]): Doc[] {
  const dict = docs.filter((d) => d.doc_id === "FAQ-000");
  const faq = docs
    .filter((d) => (d.collection || "sop") === "faq" && d.doc_id !== "FAQ-000")
    .sort(
      (a, b) =>
        (a.sort_order ?? 9999) - (b.sort_order ?? 9999) || a.doc_id.localeCompare(b.doc_id),
    );
  const sop = docs
    .filter((d) => (d.collection || "sop") === "sop")
    .sort(
      (a, b) =>
        a.domain.localeCompare(b.domain) ||
        (a.sort_order ?? 9999) - (b.sort_order ?? 9999) ||
        a.doc_id.localeCompare(b.doc_id),
    );
  return [...dict, ...faq, ...sop];
}

export default function ReviewerWorkbench() {
  const [allDocs, setAllDocs] = useState<Doc[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  // Admins only, session only: include pages that are not yet rewritten.
  const [includeNotRewritten, setIncludeNotRewritten] = useState(false);
  const docs = useMemo(() => {
    const ready = orderDocs(allDocs.filter((d) => d.canon_ready));
    if (!isAdmin || !includeNotRewritten) return ready;
    const rest = orderDocs(allDocs.filter((d) => !d.canon_ready));
    return [...ready, ...rest];
  }, [allDocs, includeNotRewritten, isAdmin]);
  const [loading, setLoading] = useState(true);
  const [statuses, setStatuses] = useState<Record<string, ReviewerStatus>>({});
  const [statusesLoaded, setStatusesLoaded] = useState(false);
  const [pos, setPos] = useState<number>(0);
  const [initialPosApplied, setInitialPosApplied] = useState(false);
  const [showWelcome, setShowWelcome] = useState(
    () => localStorage.getItem("reviewer_welcome_dismissed_v2") !== "1",
  );
  const bumpNoteRef = useRef<((c: "confusion" | "suggestion") => void) | null>(null);


  const readingScrollRef = useRef<HTMLDivElement | null>(null);
  const editScrollRef = useRef<HTMLDivElement | null>(null);
  const syncingRef = useRef<"reading" | "edit" | null>(null);
  const editorRef = useRef<ReviewerEditHandle | null>(null);
  // Per-doc flag: did the reviewer submit an edit for this doc this session?
  const [editSubmittedForDoc, setEditSubmittedForDoc] = useState<Record<string, boolean>>({});

  const fetchDocs = async (showSpinner: boolean) => {
    if (showSpinner) setLoading(true);
    const { data } = await supabase
      .from("sop_documents" as any)
      .select(
        "id,doc_id,collection,domain,title,version,body_markdown,source_refs,sort_order,status,canon_ready,updated_at",
      )
      .eq("status", "published")
      .order("updated_at", { ascending: false });
    setAllDocs(((data as any[]) || []) as Doc[]);
    if (showSpinner) setLoading(false);
  };

  // Shared progress: the effective status for a doc is the most recently
  // updated row for that doc from ANY reviewer or admin. Rows remain
  // attributed to their author for the Gradebook breakdown, but the
  // workbench treats the library as one shared review effort.
  const fetchStatuses = useCallback(async () => {
    const { data } = await supabase
      .from("reviewer_doc_progress" as any)
      .select("doc_id,status,updated_at")
      .order("updated_at", { ascending: true });
    const map: Record<string, ReviewerStatus> = {};
    for (const row of ((data as any[]) || [])) {
      // Ascending order means the last write wins.
      map[row.doc_id] = row.status as ReviewerStatus;
    }
    setStatuses(map);
    setStatusesLoaded(true);
  }, []);

  useEffect(() => {
    fetchDocs(true);
    fetchStatuses();
    supabase.auth.getUser().then(({ data: userData }) => {
      const uid = userData?.user?.id;
      if (!uid) return;
      supabase
        .from("user_roles" as any)
        .select("role")
        .eq("user_id", uid)
        .then(({ data }) =>
          setIsAdmin(
            ((data as any[]) || []).some(
              (r) => r.role === "admin" || r.role === "super_admin",
            ),
          ),
        );
    });
    const onFocus = () => {
      fetchDocs(false);
      fetchStatuses();
    };
    const onVis = () => {
      if (document.visibilityState === "visible") {
        fetchDocs(false);
        fetchStatuses();
      }
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply initial position: saved localStorage pos if present, else first
  // unfinished document. Runs once, after both docs and statuses are ready.
  useEffect(() => {
    if (initialPosApplied) return;
    if (loading || !statusesLoaded || docs.length === 0) return;
    let target = 0;
    let saved: number | null = null;
    try {
      const raw = localStorage.getItem(QUEUE_KEY);
      if (raw != null && raw !== "") saved = Number(raw);
    } catch {}
    if (saved != null && Number.isFinite(saved) && saved >= 0 && saved < docs.length) {
      target = saved;
    } else {
      const idx = docs.findIndex((d) => !isFinished(statuses[d.doc_id]));
      target = idx === -1 ? 0 : idx;
    }
    setPos(target);
    setInitialPosApplied(true);
  }, [loading, statusesLoaded, docs, statuses, initialPosApplied]);

  const current = docs[pos];
  const total = docs.length;
  const completedCount = useMemo(
    () => docs.filter((d) => isFinished(statuses[d.doc_id])).length,
    [docs, statuses],
  );
  const progress = total > 0 ? Math.round((completedCount / total) * 100) : 0;
  const anyUnfinished = useMemo(
    () => docs.some((d) => !isFinished(statuses[d.doc_id])),
    [docs, statuses],
  );

  const persistPos = (n: number) => {
    try {
      localStorage.setItem(QUEUE_KEY, String(n));
    } catch {}
  };

  // Persist a status to the database and update local state.
  const setStatusFor = useCallback(async (docId: string, s: ReviewerStatus) => {
    setStatuses((prev) => ({ ...prev, [docId]: s }));
    const { data: userData } = await supabase.auth.getUser();
    const uid = userData?.user?.id;
    if (!uid) return;
    const { error } = await supabase
      .from("reviewer_doc_progress" as any)
      .upsert(
        { user_id: uid, doc_id: docId, status: s, updated_at: new Date().toISOString() },
        { onConflict: "user_id,doc_id" },
      );
    if (error) console.error("[reviewer progress upsert]", error);
  }, []);

  // Free manual navigation — always moves through the full ordered list.
  // Uses functional setPos so the click always sees the latest position,
  // regardless of any stale closure captured from an earlier render.
  const goto = useCallback(
    (delta: number) => {
      setPos((p) => {
        const n = Math.max(0, Math.min(docs.length - 1, p + delta));
        persistPos(n);
        return n;
      });
    },
    [docs.length],
  );

  // Auto-advance to the next unfinished doc (status absent or 'redo'),
  // wrapping from the current position. If none remain, stay put; the UI
  // renders a completion state.
  const advanceToNextUnfinished = useCallback(() => {
    setPos((p) => {
      if (docs.length === 0) return p;
      for (let i = 1; i <= docs.length; i++) {
        const idx = (p + i) % docs.length;
        if (!isFinished(statuses[docs[idx].doc_id])) {
          persistPos(idx);
          return idx;
        }
      }
      return p;
    });
  }, [docs, statuses]);

  // Done guard: true while the unsent-rewrite AlertDialog is open.
  const [doneGuardOpen, setDoneGuardOpen] = useState(false);
  const [doneGuardBusy, setDoneGuardBusy] = useState(false);

  // Shared tail of the Done flow: compute the reviewer status, persist it,
  // and advance to the next unfinished page.
  const finishDone = async (doc: Doc, editSubmittedNow: boolean) => {
    const hasExistingProposal = !!editorRef.current?.getSnapshot().hasProposal;
    const editedFlag =
      editSubmittedNow || !!editSubmittedForDoc[doc.doc_id] || hasExistingProposal;
    const counts = (window as any).__reviewerNoteCounts?.[doc.doc_id] ?? {
      confusion: 0,
      suggestion: 0,
    };
    const next: ReviewerStatus = editedFlag
      ? "edited"
      : counts.confusion > 0
        ? "confusing"
        : counts.suggestion > 0
          ? "suggested"
          : "understood";
    await setStatusFor(doc.doc_id, next);
    advanceToNextUnfinished();
  };

  // True when this reviewer has a draft or needs_revision proposal on this
  // page whose body differs from the live document.
  const findDifferingDraft = async (doc: Doc): Promise<boolean> => {
    const { data: userData } = await supabase.auth.getUser();
    const uid = userData?.user?.id;
    if (!uid) return false;
    const { data } = await supabase
      .from("doc_edit_proposals" as any)
      .select("proposed_body")
      .eq("doc_id", doc.doc_id)
      .eq("editor_id", uid)
      .in("status", ["draft", "needs_revision"]);
    const live = (doc.body_markdown || "").trim();
    return (((data as any[]) || []) as { proposed_body: string | null }[]).some(
      (p) => (p.proposed_body ?? "").trim() !== live,
    );
  };

  // Scroll sync the two panes proportionally.
  useEffect(() => {
    const a = readingScrollRef.current;
    const b = editScrollRef.current;
    if (!a || !b) return;
    const mk =
      (src: HTMLDivElement, dst: HTMLDivElement, name: "reading" | "edit") => () => {
        if (syncingRef.current && syncingRef.current !== name) return;
        syncingRef.current = name;
        const srcMax = src.scrollHeight - src.clientHeight;
        const dstMax = dst.scrollHeight - dst.clientHeight;
        if (srcMax <= 0 || dstMax <= 0) {
          syncingRef.current = null;
          return;
        }
        const ratio = src.scrollTop / srcMax;
        dst.scrollTop = ratio * dstMax;
        requestAnimationFrame(() => {
          syncingRef.current = null;
        });
      };
    const onA = mk(a, b, "reading");
    const onB = mk(b, a, "edit");
    a.addEventListener("scroll", onA);
    b.addEventListener("scroll", onB);
    return () => {
      a.removeEventListener("scroll", onA);
      b.removeEventListener("scroll", onB);
    };
  }, [current?.doc_id, loading]);

  return (
    <MainLayout>
      <div className="max-w-[1400px] mx-auto p-4 md:p-6 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-serif font-semibold">Reviewer Workbench</h1>
            <p className="text-sm text-muted-foreground">
              Left: read, and highlight any words to flag them. Right: rewrite anything that would read better.
            </p>

          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => setShowWelcome(true)} title="Help">
              <HelpCircle className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-2 min-w-[220px]">
              <Progress value={progress} className="w-40" />
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                {completedCount} of {total}
              </span>
            </div>
            {isAdmin && (
              <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
                <Switch
                  checked={includeNotRewritten}
                  onCheckedChange={(v) => {
                    setIncludeNotRewritten(v);
                    setPos(0);
                    persistPos(0);
                  }}
                />
                Include pages not yet rewritten
              </label>
            )}
          </div>
        </div>

        {showWelcome && (
          <ReviewerWelcome
            onDismiss={() => {
              setShowWelcome(false);
              localStorage.setItem("reviewer_welcome_dismissed_v2", "1");
            }}
          />

        )}

        {loading ? (
          <div className="flex justify-center py-24">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : !current ? (
          <Card className="p-8 text-center text-muted-foreground">
            No published documents to review.
          </Card>
        ) : (
          <>
            {includeNotRewritten && !current.canon_ready && (
              <div className="border-t border-border pt-3 text-sm text-muted-foreground">
                Not yet rewritten
              </div>
            )}

            {/* Header row per document */}
            <Card className="p-4 md:p-5">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                  <Badge variant="secondary">{current.domain}</Badge>
                  <span className="text-xs text-muted-foreground font-mono">
                    {current.doc_id}
                  </span>
                  <span className="text-xs text-muted-foreground">v{current.version}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => goto(-1)}
                    disabled={pos <= 0}
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" /> Prev
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => goto(1)}
                    disabled={pos >= docs.length - 1}
                  >
                    Next <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
              <h1 className="grt-article-title font-serif mt-3 mb-0">{current.title}</h1>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* LEFT: reading pane */}
              <Card className="p-4 md:p-6">
                <div
                  ref={readingScrollRef}
                  className="h-[75vh] overflow-auto pr-1"
                >
                  <div className="prose-container">
                    <DocBodyBoundary key={current.doc_id}>
                      <SopMarkdown
                        body={current.body_markdown || ""}
                        docId={current.doc_id}
                      />
                    </DocBodyBoundary>
                  </div>
                </div>
                <SelectionFlagger
                  key={current.doc_id}
                  containerRef={readingScrollRef}
                  docId={current.doc_id}
                  collection={(current.collection || "sop") as "faq" | "sop"}
                  docVersion={current.version}
                  onFiled={(c) => bumpNoteRef.current?.(c)}
                />
              </Card>


              {/* RIGHT: edit pane */}
              <Card className="p-4 md:p-6">
                <ReviewerEditView
                  key={current.doc_id}
                  ref={editorRef}
                  doc={current}
                  scrollRef={editScrollRef}
                  onSubmittedForReview={() => {
                    setEditSubmittedForDoc((m) => ({ ...m, [current.doc_id]: true }));
                  }}
                />
              </Card>
            </div>

            {!anyUnfinished && (
              <Card className="p-6 text-center bg-emerald-50 border-emerald-200">
                <CheckCircle2 className="h-6 w-6 mx-auto text-emerald-700 mb-2" />
                <p className="text-sm font-medium">Every document is done. Thank you.</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Use the arrows above to revisit any document.
                </p>
              </Card>
            )}

            {/* Actions row */}
            <ActionRow
              key={current.doc_id}
              docId={current.doc_id}
              collection={(current.collection || "sop") as "faq" | "sop"}
              version={current.version}
              bumpRef={bumpNoteRef}

              onDone={async () => {
                const handle = editorRef.current;
                if (handle) {
                  const snap = handle.getSnapshot();
                  if (snap.dirty && snap.hasIssues) {
                    toast.error(
                      "Your edit has checks to fix. Fix them or undo your changes, then press Done.",
                    );
                    return;
                  }
                }
                // Unsent rewrite guard: a saved draft (or needs_revision) that
                // differs from the live page gets a keep-or-submit dialog.
                if (await findDifferingDraft(current)) {
                  setDoneGuardOpen(true);
                  return;
                }
                let editSubmittedNow = false;
                if (handle && handle.getSnapshot().dirty) {
                  const res = await handle.submit();
                  if (!res.submitted) return;
                  editSubmittedNow = true;
                }
                await finishDone(current, editSubmittedNow);
              }}
            />

            <AlertDialog
              open={doneGuardOpen}
              onOpenChange={(o) => {
                if (!doneGuardBusy) setDoneGuardOpen(o);
              }}
            >
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    You have an unsent rewrite on this page
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    Send it to the checker now, or keep it as a draft and come back later.
                    Either way it is saved.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel
                    disabled={doneGuardBusy}
                    onClick={async (e) => {
                      e.preventDefault();
                      setDoneGuardBusy(true);
                      try {
                        setDoneGuardOpen(false);
                        await finishDone(current, false);
                      } finally {
                        setDoneGuardBusy(false);
                      }
                    }}
                  >
                    Keep as draft
                  </AlertDialogCancel>
                  <AlertDialogAction
                    disabled={doneGuardBusy}
                    onClick={async (e) => {
                      e.preventDefault();
                      setDoneGuardBusy(true);
                      try {
                        const res = await editorRef.current?.submit();
                        if (res?.submitted) {
                          setDoneGuardOpen(false);
                          await finishDone(current, true);
                        }
                      } finally {
                        setDoneGuardBusy(false);
                      }
                    }}
                  >
                    {doneGuardBusy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                    Send rewrite
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        )}
      </div>
    </MainLayout>
  );
}

function ActionRow({
  docId,
  collection,
  version,
  onDone,
  bumpRef,
}: {
  docId: string;
  collection: "faq" | "sop";
  version: string;
  onDone: () => void | Promise<void>;
  bumpRef?: React.MutableRefObject<((c: "confusion" | "suggestion") => void) | null>;
}) {

  const [openKind, setOpenKind] = useState<null | "confusion" | "suggestion">(null);
  const [counts, setCounts] = useState<{ confusion: number; suggestion: number }>({
    confusion: 0,
    suggestion: 0,
  });
  const [doneBusy, setDoneBusy] = useState(false);

  // Let the selection flag in the reading pane bump the same counters.
  useEffect(() => {
    if (!bumpRef) return;
    bumpRef.current = (c) =>
      setCounts((prev) =>
        c === "confusion"
          ? { ...prev, confusion: prev.confusion + 1 }
          : { ...prev, suggestion: prev.suggestion + 1 },
      );
    return () => {
      bumpRef.current = null;
    };
  }, [bumpRef]);



  // Publish counts on window so the workbench Done handler can read the
  // latest values without threading additional callbacks up. Cheap and local.
  useEffect(() => {
    const w = window as any;
    w.__reviewerNoteCounts = w.__reviewerNoteCounts || {};
    w.__reviewerNoteCounts[docId] = counts;
  }, [docId, counts]);

  useEffect(() => {
    setOpenKind(null);
    setCounts({ confusion: 0, suggestion: 0 });
    (async () => {
      const { data: userData } = await supabase.auth.getUser();
      const uid = userData?.user?.id;
      if (!uid) return;
      const { data } = await supabase
        .from("doc_feedback" as any)
        .select("category")
        .eq("doc_id", docId)
        .eq("user_id", uid);
      const rows = ((data as any[]) || []) as { category: string | null }[];
      const confusion = rows.filter((r) => (r.category ?? "confusion") === "confusion").length;
      const suggestion = rows.filter((r) => r.category === "suggestion").length;
      setCounts({ confusion, suggestion });
    })();
  }, [docId]);

  const totalNotes = counts.confusion + counts.suggestion;

  const scrollToWidget = () => {
    setTimeout(() => {
      document
        .getElementById("reviewer-feedback-anchor")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  };

  return (
    <Card className="p-4 md:p-5">
      <p className="text-xs text-muted-foreground mb-3">
        Highlight words on the left to flag them. Rewrite on the right and send as often as you like. Press Done only when you are finished with the page.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          className="bg-emerald-600 hover:bg-emerald-700 text-white"
          disabled={doneBusy}
          onClick={async () => {
            setDoneBusy(true);
            try {
              await onDone();
            } finally {
              setDoneBusy(false);
            }
          }}
        >
          {doneBusy ? (
            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
          ) : (
            <CheckCircle2 className="h-4 w-4 mr-1" />
          )}
          Done with this page
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setOpenKind("confusion");
            scrollToWidget();
          }}
        >
          <AlertCircle className="h-4 w-4 mr-1" /> Something confused me
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setOpenKind("suggestion");
            scrollToWidget();
          }}
        >
          <Lightbulb className="h-4 w-4 mr-1" /> I have a suggestion
        </Button>
        {totalNotes > 0 && (
          <span className="text-[11px] text-muted-foreground ml-auto">
            Notes sent on this page: {totalNotes}
          </span>
        )}
      </div>
      <p className="text-[11px] text-muted-foreground mt-2">
        Done marks this page reviewed and takes it off your list.
      </p>

      <div id="reviewer-feedback-anchor" className="mt-4">
        {openKind && (
          <DocFeedbackWidget
            key={`${docId}:${openKind}`}
            docId={docId}
            collection={collection}
            docVersion={version}
            category={openKind}
            open={true}
            onOpenChange={(o) => {
              if (!o) setOpenKind(null);
            }}
            requireNote={openKind === "suggestion"}
            promptLabel={
              openKind === "suggestion" ? "What would help?" : "What tripped you up?"
            }
            resetAfterFileMs={1600}

            onFiled={() => {
              setCounts((c) =>
                openKind === "confusion"
                  ? { ...c, confusion: c.confusion + 1 }
                  : { ...c, suggestion: c.suggestion + 1 },
              );
            }}
          />
        )}
      </div>
    </Card>
  );
}
